/**
 * RecruitmentPage — Module Recrutement standalone (v4.4.0)
 * 5 onglets : Dashboard, Offres, Candidats, Entretiens, CV Drive
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  UserPlus, Briefcase, Users, Calendar, FileText, Plus, Search, X, Edit, Trash2,
  ChevronDown, ChevronRight, Star, MessageSquare, Phone, Mail, MapPin,
  ExternalLink, Eye, Upload, BarChart3, Clock, CheckCircle, XCircle,
  AlertTriangle, Building, Loader, Copy, Globe, Filter, Send, Archive
} from 'lucide-react';
import {
  getRecruitmentOffers, getRecruitmentOffersMulti, createRecruitmentOffer, updateRecruitmentOffer, deleteRecruitmentOffer,
  createRecruitmentPublication, updateRecruitmentPublication, deleteRecruitmentPublication,
  getRecruitmentApplications, getRecruitmentApplicationsMulti, createRecruitmentApplication,
  updateRecruitmentApplication, deleteRecruitmentApplication, updateRecruitmentApplicationStatus,
  uploadRecruitmentCV, analyzeRecruitmentCV,
  getRecruitmentComments, createRecruitmentComment, deleteRecruitmentComment,
  getRecruitmentInterviews, getRecruitmentInterviewsByApp, createRecruitmentInterview,
  completeRecruitmentInterview, deleteRecruitmentInterview,
  getInterviewTemplates,
  getRecruitmentPendingCVs, approveRecruitmentCV, rejectRecruitmentCV,
  getRecruitmentDashboard, getEvaluationGrids,
  getRecruitmentPositions,
} from '../api';
import { useStructure } from '../contexts/StructureContext';
import { useToast } from '../contexts/NotificationContext';
import Modal from '../components/ui/Modal';
import CandidateDetail from '../components/CandidateDetail';
import LoadingSpinner from '../components/LoadingSpinner';

// ─── Constantes ───────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  nouveau:            { label: 'Nouveau',           color: 'bg-gray-100 text-gray-700',   icon: Clock },
  présélectionné:     { label: 'Présélectionné',    color: 'bg-blue-100 text-blue-700',   icon: Star },
  entretien_tel:      { label: 'Entretien tél.',    color: 'bg-indigo-100 text-indigo-700', icon: Phone },
  entretien_physique: { label: 'Entretien physique', color: 'bg-purple-100 text-purple-700', icon: Users },
  visite_structure:   { label: 'Visite structure',  color: 'bg-teal-100 text-teal-700',   icon: Building },
  offre_envoyée:      { label: 'Offre envoyée',     color: 'bg-amber-100 text-amber-700', icon: Send },
  embauché:           { label: 'Embauché',          color: 'bg-green-100 text-green-700', icon: CheckCircle },
  refusé:             { label: 'Refusé',            color: 'bg-red-100 text-red-700',     icon: XCircle },
  archivé:            { label: 'Archivé',           color: 'bg-gray-100 text-gray-400',   icon: Archive },
};

const OFFER_STATUS = {
  draft:     { label: 'Brouillon',  color: 'bg-gray-100 text-gray-600' },
  published: { label: 'Publiée',    color: 'bg-green-100 text-green-700' },
  closed:    { label: 'Clôturée',   color: 'bg-red-100 text-red-600' },
  archived:  { label: 'Archivée',   color: 'bg-gray-100 text-gray-400' },
};

const PLATFORMS = [
  { value: 'france_travail', label: 'France Travail' },
  { value: 'indeed', label: 'Indeed' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'site_web', label: 'Site web' },
  { value: 'autre', label: 'Autre' },
];

const SOURCES = ['France Travail', 'Indeed', 'LinkedIn', 'Mail', 'Spontané', 'Autre'];

const CONTRACT_TYPES = ['CDI', 'CDD', 'Stage', 'Alternance', 'Intérim'];

const ROUND_LABELS = { 1: 'Téléphonique', 2: 'Présentiel', 3: 'Visite structure' };

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';
const fmtDateTime = (d) => d ? new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

// ─── StatusBadge ──────────────────────────────────────────────────────────────

const StatusBadge = ({ status, config = STATUS_CONFIG }) => {
  const cfg = config[status] || { label: status, color: 'bg-gray-100 text-gray-700' };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
      {Icon && <Icon size={12} />}
      {cfg.label}
    </span>
  );
};

const RatingStars = ({ rating, onChange }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map(n => (
      <button key={n} type="button" onClick={() => onChange?.(n)} className={`${n <= (rating || 0) ? 'text-amber-400' : 'text-gray-300'} ${onChange ? 'cursor-pointer hover:text-amber-500' : ''}`}>
        <Star size={16} fill={n <= (rating || 0) ? 'currentColor' : 'none'} />
      </button>
    ))}
  </div>
);

// ─── Composant principal ──────────────────────────────────────────────────────

const RecruitmentPage = ({ showAllStructures = false, navPayload, onNavPayloadConsumed }) => {
  const { currentStructure, structures } = useStructure();
  const toast = useToast();

  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);

  // Data
  const [offers, setOffers] = useState([]);
  const [applications, setApplications] = useState([]);
  const [pendingCVs, setPendingCVs] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [dashboardData, setDashboardData] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [grids, setGrids] = useState([]);
  const [positions, setPositions] = useState([]);

  // UI
  const [selectedApp, setSelectedApp] = useState(null);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [showAppModal, setShowAppModal] = useState(false);
  const [showInterviewModal, setShowInterviewModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(null);
  const [editingOffer, setEditingOffer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [filterOffer, setFilterOffer] = useState('');

  const structureId = currentStructure?.id;

  // ─── Data Loading ─────────────────────────────────────────────────────────

  const loadOffers = useCallback(async () => {
    try {
      const { data } = showAllStructures
        ? await getRecruitmentOffersMulti(structures.map(s => s.id).join(','))
        : await getRecruitmentOffers({ structureId });
      setOffers(data);
    } catch (e) { console.error('loadOffers:', e); }
  }, [structureId, showAllStructures, structures]);

  const loadApplications = useCallback(async () => {
    try {
      const params = { structureId };
      if (filterStatus) params.status = filterStatus;
      if (filterOffer) params.offerId = filterOffer;
      if (filterSource) params.source = filterSource;
      const { data } = showAllStructures
        ? await getRecruitmentApplicationsMulti(structures.map(s => s.id).join(','))
        : await getRecruitmentApplications(params);
      setApplications(data);
    } catch (e) { console.error('loadApplications:', e); }
  }, [structureId, showAllStructures, structures, filterStatus, filterOffer, filterSource]);

  const loadPendingCVs = useCallback(async () => {
    try {
      const { data } = await getRecruitmentPendingCVs();
      setPendingCVs(data);
    } catch (e) { console.error('loadPendingCVs:', e); }
  }, []);

  const loadInterviews = useCallback(async () => {
    try {
      const { data } = await getRecruitmentInterviews({ structureId, upcoming: 'true' });
      setInterviews(data);
    } catch (e) { console.error('loadInterviews:', e); }
  }, [structureId]);

  const loadDashboard = useCallback(async () => {
    try {
      const { data } = await getRecruitmentDashboard({ structureId: showAllStructures ? undefined : structureId });
      setDashboardData(data);
    } catch (e) { console.error('loadDashboard:', e); }
  }, [structureId, showAllStructures]);

  const loadTemplates = useCallback(async () => {
    try {
      const { data } = await getInterviewTemplates();
      setTemplates(data);
    } catch (e) { console.error('loadTemplates:', e); }
  }, []);

  const loadGrids = useCallback(async () => {
    try {
      const { data } = await getEvaluationGrids();
      setGrids(data);
    } catch (e) { console.error('loadGrids:', e); }
  }, []);

  const loadPositions = useCallback(async () => {
    try {
      const { data } = await getRecruitmentPositions();
      setPositions(data);
    } catch (e) { console.error('loadPositions:', e); }
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadOffers(), loadApplications(), loadPendingCVs(), loadInterviews(), loadDashboard(), loadTemplates(), loadGrids(), loadPositions()])
      .finally(() => setLoading(false));
  }, [loadOffers, loadApplications, loadPendingCVs, loadInterviews, loadDashboard, loadTemplates, loadGrids, loadPositions]);

  // Auto-ouverture fiche depuis recherche globale (navPayload.applicationId | navPayload.offerId)
  useEffect(() => {
    if (!navPayload) return;
    if (navPayload.applicationId) {
      const app = applications.find(a => a.id === navPayload.applicationId);
      if (app) {
        setActiveTab('candidates');
        setSelectedApp(app);
        onNavPayloadConsumed?.();
      }
    } else if (navPayload.offerId) {
      const off = offers.find(o => o.id === navPayload.offerId);
      if (off) {
        setActiveTab('offers');
        // Pas de modal d'édition automatique — on bascule juste sur l'onglet Offres
        onNavPayloadConsumed?.();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navPayload, applications, offers]);

  // ─── Offer Handlers ───────────────────────────────────────────────────────

  const [offerForm, setOfferForm] = useState({ title: '', description: '', requirements: '', contractType: 'CDI', salary: '', workHours: '35h', location: '', structureId: '' });

  const handleSaveOffer = async () => {
    try {
      const payload = { ...offerForm, structureId: offerForm.structureId || structureId };
      if (editingOffer) {
        await updateRecruitmentOffer(editingOffer.id, payload);
        toast.success('Offre mise à jour');
      } else {
        await createRecruitmentOffer(payload);
        toast.success('Offre créée');
      }
      setShowOfferModal(false);
      setEditingOffer(null);
      setOfferForm({ title: '', description: '', requirements: '', contractType: 'CDI', salary: '', workHours: '35h', location: '', structureId: '' });
      loadOffers();
      loadDashboard();
    } catch (e) {
      toast.error('Erreur sauvegarde offre');
    }
  };

  const handleDeleteOffer = async (id) => {
    if (!window.confirm('Supprimer cette offre ?')) return;
    try {
      await deleteRecruitmentOffer(id);
      toast.success('Offre supprimée');
      loadOffers();
    } catch (e) { toast.error('Erreur suppression'); }
  };

  const handleOfferStatus = async (id, status) => {
    try {
      await updateRecruitmentOffer(id, { status });
      toast.success(`Offre ${status === 'published' ? 'publiée' : status === 'closed' ? 'clôturée' : 'mise à jour'}`);
      loadOffers();
    } catch (e) { toast.error('Erreur'); }
  };

  // ─── Publication Handlers ─────────────────────────────────────────────────

  const [pubForm, setPubForm] = useState({ platform: 'france_travail', publishedAt: '', url: '', reference: '' });
  const [pubOfferId, setPubOfferId] = useState(null);

  const handleAddPublication = async () => {
    if (!pubOfferId) return;
    try {
      await createRecruitmentPublication(pubOfferId, pubForm);
      toast.success('Publication ajoutée');
      setPubOfferId(null);
      loadOffers();
    } catch (e) { toast.error('Erreur publication'); }
  };

  const handleDeletePublication = async (offerId, pubId) => {
    try {
      await deleteRecruitmentPublication(offerId, pubId);
      toast.success('Publication supprimée');
      loadOffers();
    } catch (e) { toast.error('Erreur suppression'); }
  };

  // ─── Application Handlers ─────────────────────────────────────────────────

  const [appForm, setAppForm] = useState({
    candidateName: '', candidateEmail: '', candidatePhone: '', position: '',
    source: '', notes: '', offerId: '', diplomas: '', structureId: '', cv: null,
  });

  const handleSaveApplication = async () => {
    try {
      const data = { ...appForm, structureId: appForm.structureId || structureId };
      if (appForm.cv) data.cv = appForm.cv;
      await createRecruitmentApplication(data);
      toast.success('Candidature créée');
      setShowAppModal(false);
      setAppForm({ candidateName: '', candidateEmail: '', candidatePhone: '', position: '', source: '', notes: '', offerId: '', diplomas: '', structureId: '', cv: null });
      loadApplications();
      loadDashboard();
    } catch (e) { toast.error('Erreur création candidature'); }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await updateRecruitmentApplicationStatus(id, status);
      toast.success('Statut mis à jour');
      loadApplications();
      loadDashboard();
      if (selectedApp?.id === id) {
        setSelectedApp(prev => ({ ...prev, status }));
      }
    } catch (e) { toast.error('Erreur changement statut'); }
  };

  const handleDeleteApplication = async (id) => {
    if (!window.confirm('Supprimer cette candidature ?')) return;
    try {
      await deleteRecruitmentApplication(id);
      toast.success('Candidature supprimée');
      if (selectedApp?.id === id) setSelectedApp(null);
      loadApplications();
      loadDashboard();
    } catch (e) { toast.error('Erreur suppression'); }
  };

  // ─── Pending CV Handlers ──────────────────────────────────────────────────

  const [approveForm, setApproveForm] = useState({ structureId: '', candidateName: '', position: '', offerId: '' });

  const handleApproveCV = async () => {
    if (!showApproveModal) return;
    try {
      await approveRecruitmentCV(showApproveModal.id, { ...approveForm, structureId: approveForm.structureId || structureId });
      toast.success('CV approuvé, candidature créée');
      setShowApproveModal(null);
      loadPendingCVs();
      loadApplications();
    } catch (e) { toast.error(e.response?.data?.error || 'Erreur approbation'); }
  };

  const handleRejectCV = async (id) => {
    if (!window.confirm('Rejeter ce CV ?')) return;
    try {
      await rejectRecruitmentCV(id);
      toast.success('CV rejeté');
      loadPendingCVs();
    } catch (e) { toast.error('Erreur rejet'); }
  };

  // ─── Interview Handlers ───────────────────────────────────────────────────

  const [interviewForm, setInterviewForm] = useState({ applicationId: '', round: 1, templateId: '', scheduledAt: '', interviewers: '', structureId: '' });

  const handleCreateInterview = async () => {
    try {
      await createRecruitmentInterview(interviewForm.applicationId, {
        round: interviewForm.round,
        templateId: interviewForm.templateId || null,
        scheduledAt: interviewForm.scheduledAt || null,
        interviewers: interviewForm.interviewers || null,
        structureId: interviewForm.structureId || null,
      });
      toast.success('Entretien planifié');
      setShowInterviewModal(false);
      loadInterviews();
      loadApplications();
    } catch (e) { toast.error('Erreur planification'); }
  };

  // ─── Filtered Applications ────────────────────────────────────────────────

  const filteredApps = applications.filter(a => {
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      if (!a.candidateName.toLowerCase().includes(s) && !a.position?.toLowerCase().includes(s) && !a.candidateEmail?.toLowerCase().includes(s)) return false;
    }
    return true;
  });

  // ─── TABS ─────────────────────────────────────────────────────────────────

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'offers', label: 'Offres', icon: Briefcase, count: offers.filter(o => o.status === 'published').length },
    { id: 'candidates', label: 'Candidats', icon: Users, count: applications.filter(a => !['embauché', 'refusé', 'archivé'].includes(a.status)).length },
    { id: 'interviews', label: 'Entretiens', icon: Calendar, count: interviews.length },
    { id: 'pending', label: 'CV Drive', icon: FileText, count: pendingCVs.length },
  ];

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-violet-100 rounded-lg">
            <UserPlus className="text-violet-600" size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Recrutement</h1>
            <p className="text-sm text-gray-500">Gestion des offres, candidatures et entretiens</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 rounded-lg p-1 overflow-x-auto">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                activeTab === tab.id ? 'bg-white text-violet-700 shadow-sm' : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <Icon size={16} />
              {tab.label}
              {tab.count > 0 && (
                <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs ${activeTab === tab.id ? 'bg-violet-100 text-violet-700' : 'bg-gray-200 text-gray-600'}`}>
                  {tab.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {loading && (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="large" text="Chargement..." />
        </div>
      )}

      {!loading && (
        <>
          {/* ═══ DASHBOARD TAB ═══ */}
          {activeTab === 'dashboard' && dashboardData && (
            <DashboardTab data={dashboardData} />
          )}

          {/* ═══ OFFERS TAB ═══ */}
          {activeTab === 'offers' && (
            <OffersTab
              offers={offers}
              structures={structures}
              showAllStructures={showAllStructures}
              onEdit={(offer) => { setEditingOffer(offer); setOfferForm({ title: offer.title, description: offer.description, requirements: offer.requirements || '', contractType: offer.contractType, salary: offer.salary || '', workHours: offer.workHours || '', location: offer.location || '', structureId: offer.structureId || '' }); setShowOfferModal(true); }}
              onDelete={handleDeleteOffer}
              onStatusChange={handleOfferStatus}
              onAddPublication={(offerId) => { setPubOfferId(offerId); setPubForm({ platform: 'france_travail', publishedAt: new Date().toISOString().split('T')[0], url: '', reference: '' }); }}
              onDeletePublication={handleDeletePublication}
              onAdd={() => { setEditingOffer(null); setOfferForm({ title: '', description: '', requirements: '', contractType: 'CDI', salary: '', workHours: '35h', location: '', structureId: '' }); setShowOfferModal(true); }}
            />
          )}

          {/* ═══ CANDIDATES TAB ═══ */}
          {activeTab === 'candidates' && (
            <div>
              {/* Toolbar */}
              <div className="flex flex-wrap gap-3 mb-4">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
                  <input type="text" placeholder="Rechercher un candidat..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm" />
                </div>
                <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
                  <option value="">Tous les statuts</option>
                  {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
                <select value={filterSource} onChange={e => setFilterSource(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
                  <option value="">Toutes les sources</option>
                  {SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                {offers.length > 0 && (
                  <select value={filterOffer} onChange={e => setFilterOffer(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
                    <option value="">Toutes les offres</option>
                    {offers.map(o => <option key={o.id} value={o.id}>{o.title}</option>)}
                  </select>
                )}
              </div>

              {/* Table */}
              <div className="bg-white rounded-lg shadow overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Candidat</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Poste / Offre</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Source</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Statut</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Note</th>
                      {showAllStructures && <th className="text-left px-4 py-3 font-medium text-gray-600">Structure</th>}
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Date</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredApps.map(app => (
                      <tr key={app.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedApp(app)}>
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-800">{app.candidateName}</div>
                          {app.candidateEmail && <div className="text-xs text-gray-400">{app.candidateEmail}</div>}
                        </td>
                        <td className="px-4 py-3">
                          <div>{app.position}</div>
                          {app.offer && <div className="text-xs text-violet-500">{app.offer.title}</div>}
                        </td>
                        <td className="px-4 py-3 text-gray-600">{app.source || '—'}</td>
                        <td className="px-4 py-3"><StatusBadge status={app.status} /></td>
                        <td className="px-4 py-3"><RatingStars rating={app.rating} /></td>
                        {showAllStructures && <td className="px-4 py-3 text-gray-500 text-xs">{app.structure?.name}</td>}
                        <td className="px-4 py-3 text-gray-500 text-xs">{fmtDate(app.createdAt)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1 text-gray-400">
                            {app._count?.comments > 0 && <span className="flex items-center gap-0.5 text-xs"><MessageSquare size={12} />{app._count.comments}</span>}
                            {app._count?.interviews > 0 && <span className="flex items-center gap-0.5 text-xs"><Calendar size={12} />{app._count.interviews}</span>}
                            {app.cvUrl && <FileText size={14} className="text-blue-400" />}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filteredApps.length === 0 && (
                      <tr><td colSpan={showAllStructures ? 8 : 7} className="px-4 py-8 text-center text-gray-400">Aucune candidature</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ═══ INTERVIEWS TAB ═══ */}
          {activeTab === 'interviews' && (
            <InterviewsTab
              interviews={interviews}
              templates={templates}
              applications={applications}
              structures={structures}
              onRefresh={() => { loadInterviews(); loadApplications(); }}
              onPlanInterview={(appId) => { setInterviewForm({ applicationId: appId || '', round: 1, templateId: '', scheduledAt: '', interviewers: '', structureId: '' }); setShowInterviewModal(true); }}
            />
          )}

          {/* ═══ PENDING CVs TAB ═══ */}
          {activeTab === 'pending' && (
            <PendingCVsTab
              pendingCVs={pendingCVs}
              onApprove={(cv) => { setShowApproveModal(cv); setApproveForm({ structureId: structureId || '', candidateName: cv.candidateName || '', position: '', offerId: cv.offerId || '' }); }}
              onReject={handleRejectCV}
              offers={offers}
              structures={structures}
            />
          )}
        </>
      )}

      {/* ═══ CANDIDATE DETAIL PANEL ═══ */}
      {selectedApp && (
        <CandidateDetail
          application={selectedApp}
          grids={grids}
          templates={templates}
          structures={structures}
          offers={offers}
          positions={positions}
          onClose={() => setSelectedApp(null)}
          onStatusChange={handleStatusChange}
          onDelete={handleDeleteApplication}
          onRefresh={() => { loadApplications(); loadDashboard(); }}
        />
      )}

      {/* ═══ OFFER MODAL ═══ */}
      <Modal isOpen={showOfferModal} onClose={() => { setShowOfferModal(false); setEditingOffer(null); }} title={editingOffer ? 'Modifier l\'offre' : 'Nouvelle offre'} size="lg">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Titre du poste *</label>
            <input type="text" value={offerForm.title} onChange={e => setOfferForm(f => ({ ...f, title: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Assistant(e) Accueil Petite Enfance" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type de contrat *</label>
              <select value={offerForm.contractType} onChange={e => setOfferForm(f => ({ ...f, contractType: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
                {CONTRACT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Salaire</label>
              <input type="text" value={offerForm.salary} onChange={e => setOfferForm(f => ({ ...f, salary: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="1801-1900€/mois" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Horaires</label>
              <input type="text" value={offerForm.workHours} onChange={e => setOfferForm(f => ({ ...f, workHours: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="35h" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Lieu</label>
              <input type="text" value={offerForm.location} onChange={e => setOfferForm(f => ({ ...f, location: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Colombes / Asnières-sur-Seine" />
            </div>
          </div>
          {(showAllStructures || structures.length > 1) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Structure</label>
              <select value={offerForm.structureId} onChange={e => setOfferForm(f => ({ ...f, structureId: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
                <option value="">Toutes les structures</option>
                {structures.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
            <textarea rows={6} value={offerForm.description} onChange={e => setOfferForm(f => ({ ...f, description: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Description du poste..." />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Prérequis</label>
            <textarea rows={3} value={offerForm.requirements} onChange={e => setOfferForm(f => ({ ...f, requirements: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Diplômes, expérience, compétences..." />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => { setShowOfferModal(false); setEditingOffer(null); }} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Annuler</button>
            <button onClick={handleSaveOffer} disabled={!offerForm.title || !offerForm.description} className="px-4 py-2 text-sm bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50">
              {editingOffer ? 'Enregistrer' : 'Créer l\'offre'}
            </button>
          </div>
        </div>
      </Modal>

      {/* ═══ APPLICATION MODAL ═══ */}
      <Modal isOpen={showAppModal} onClose={() => setShowAppModal(false)} title="Nouvelle candidature" size="lg" preventClose={true}>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nom du candidat *</label>
              <input type="text" value={appForm.candidateName} onChange={e => setAppForm(f => ({ ...f, candidateName: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Poste *</label>
              <select value={appForm.position} onChange={e => setAppForm(f => ({ ...f, position: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
                <option value="">Selectionner un poste...</option>
                {positions.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input type="email" value={appForm.candidateEmail} onChange={e => setAppForm(f => ({ ...f, candidateEmail: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
              <input type="tel" value={appForm.candidatePhone} onChange={e => setAppForm(f => ({ ...f, candidatePhone: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
              <select value={appForm.source} onChange={e => setAppForm(f => ({ ...f, source: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
                <option value="">—</option>
                {SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Offre liée</label>
              <select value={appForm.offerId} onChange={e => setAppForm(f => ({ ...f, offerId: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
                <option value="">Aucune</option>
                {offers.filter(o => o.status === 'published').map(o => <option key={o.id} value={o.id}>{o.title}</option>)}
              </select>
            </div>
          </div>
          {(showAllStructures || structures.length > 1) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Structure *</label>
              <select value={appForm.structureId} onChange={e => setAppForm(f => ({ ...f, structureId: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
                <option value="">Sélectionner...</option>
                {structures.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Diplômes</label>
            <input type="text" value={appForm.diplomas} onChange={e => setAppForm(f => ({ ...f, diplomas: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="CAP AEPE, BEP CSS..." />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">CV (PDF)</label>
            <input type="file" accept=".pdf" onChange={e => setAppForm(f => ({ ...f, cv: e.target.files[0] }))} className="w-full text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea rows={2} value={appForm.notes} onChange={e => setAppForm(f => ({ ...f, notes: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => setShowAppModal(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Annuler</button>
            <button onClick={handleSaveApplication} disabled={!appForm.candidateName || !appForm.position} className="px-4 py-2 text-sm bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50">
              Créer la candidature
            </button>
          </div>
        </div>
      </Modal>

      {/* ═══ INTERVIEW MODAL ═══ */}
      <Modal isOpen={showInterviewModal} onClose={() => setShowInterviewModal(false)} title="Planifier un entretien" size="md" preventClose={true}>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Candidat *</label>
            <select value={interviewForm.applicationId} onChange={e => setInterviewForm(f => ({ ...f, applicationId: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
              <option value="">Sélectionner...</option>
              {applications.filter(a => !['embauché', 'refusé', 'archivé'].includes(a.status)).map(a => (
                <option key={a.id} value={a.id}>{a.candidateName} — {a.position}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Round *</label>
              <select value={interviewForm.round} onChange={e => setInterviewForm(f => ({ ...f, round: parseInt(e.target.value) }))} className="w-full border rounded-lg px-3 py-2 text-sm">
                <option value={1}>1 — Téléphonique</option>
                <option value={2}>2 — Présentiel</option>
                <option value={3}>3 — Visite structure</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date / heure</label>
              <input type="datetime-local" value={interviewForm.scheduledAt} onChange={e => setInterviewForm(f => ({ ...f, scheduledAt: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Intervieweur(s)</label>
            <input type="text" value={interviewForm.interviewers} onChange={e => setInterviewForm(f => ({ ...f, interviewers: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Jérôme, Nicolas" />
          </div>
          {interviewForm.round === 3 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Structure à visiter</label>
              <select value={interviewForm.structureId} onChange={e => setInterviewForm(f => ({ ...f, structureId: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
                <option value="">Sélectionner...</option>
                {structures.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          )}
          {templates.filter(t => t.round === interviewForm.round).length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Template de questions</label>
              <select value={interviewForm.templateId} onChange={e => setInterviewForm(f => ({ ...f, templateId: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
                <option value="">Sans template</option>
                {templates.filter(t => t.round === interviewForm.round).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => setShowInterviewModal(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Annuler</button>
            <button onClick={handleCreateInterview} disabled={!interviewForm.applicationId} className="px-4 py-2 text-sm bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50">
              Planifier
            </button>
          </div>
        </div>
      </Modal>

      {/* ═══ PUBLICATION MODAL ═══ */}
      <Modal isOpen={!!pubOfferId} onClose={() => setPubOfferId(null)} title="Ajouter une publication" size="sm">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Plateforme</label>
            <select value={pubForm.platform} onChange={e => setPubForm(f => ({ ...f, platform: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
              {PLATFORMS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date de publication</label>
            <input type="date" value={pubForm.publishedAt} onChange={e => setPubForm(f => ({ ...f, publishedAt: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">URL de l'annonce</label>
            <input type="url" value={pubForm.url} onChange={e => setPubForm(f => ({ ...f, url: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="https://..." />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Référence</label>
            <input type="text" value={pubForm.reference} onChange={e => setPubForm(f => ({ ...f, reference: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="N° annonce" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => setPubOfferId(null)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Annuler</button>
            <button onClick={handleAddPublication} className="px-4 py-2 text-sm bg-violet-600 text-white rounded-lg hover:bg-violet-700">Ajouter</button>
          </div>
        </div>
      </Modal>

      {/* ═══ APPROVE CV MODAL ═══ */}
      <Modal isOpen={!!showApproveModal} onClose={() => setShowApproveModal(null)} title="Approuver le CV" size="md">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">Fichier : <strong>{showApproveModal?.fileName}</strong></p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nom du candidat *</label>
            <input type="text" value={approveForm.candidateName} onChange={e => setApproveForm(f => ({ ...f, candidateName: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Poste *</label>
            <select value={approveForm.position} onChange={e => setApproveForm(f => ({ ...f, position: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
              <option value="">Selectionner un poste...</option>
              {positions.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Structure *</label>
            <select value={approveForm.structureId} onChange={e => setApproveForm(f => ({ ...f, structureId: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
              <option value="">Sélectionner...</option>
              {structures.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Offre liée</label>
            <select value={approveForm.offerId} onChange={e => setApproveForm(f => ({ ...f, offerId: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
              <option value="">Aucune</option>
              {offers.filter(o => o.status === 'published').map(o => <option key={o.id} value={o.id}>{o.title}</option>)}
            </select>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => setShowApproveModal(null)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Annuler</button>
            <button onClick={handleApproveCV} disabled={!approveForm.candidateName || !approveForm.position || !approveForm.structureId} className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
              Approuver et créer la candidature
            </button>
          </div>
        </div>
      </Modal>

      {/* ═══ FAB ═══ */}
      <div className="fixed bottom-6 right-6 flex flex-col gap-2">
        <button
          onClick={() => { setShowAppModal(true); setAppForm({ candidateName: '', candidateEmail: '', candidatePhone: '', position: '', source: '', notes: '', offerId: '', diplomas: '', structureId: '', cv: null }); }}
          className="w-14 h-14 bg-violet-600 text-white rounded-full shadow-lg flex items-center justify-center hover:bg-violet-700 transition-colors"
          title="Ajouter un candidat"
        >
          <Plus size={24} />
        </button>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// SUB-COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════

// ─── Dashboard Tab ────────────────────────────────────────────────────────────

const DashboardTab = ({ data }) => {
  const { kpis, pipeline, sources, monthlyActivity } = data;

  const pipelineOrder = ['nouveau', 'présélectionné', 'entretien_tel', 'entretien_physique', 'visite_structure', 'offre_envoyée', 'embauché', 'refusé'];
  const maxPipeline = Math.max(...Object.values(pipeline), 1);

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Offres actives', value: kpis.activeOffers, icon: Briefcase, color: 'violet' },
          { label: 'Candidatures actives', value: kpis.activeApplications, icon: Users, color: 'blue' },
          { label: 'Entretiens planifiés', value: kpis.scheduledInterviews, icon: Calendar, color: 'indigo' },
          { label: 'Taux d\'embauche', value: `${kpis.hireRate}%`, icon: CheckCircle, color: 'green' },
        ].map((kpi, i) => {
          const Icon = kpi.icon;
          return (
            <div key={i} className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center justify-between mb-2">
                <Icon size={20} className={`text-${kpi.color}-500`} />
                <span className="text-2xl font-bold text-gray-800">{kpi.value}</span>
              </div>
              <p className="text-xs text-gray-500">{kpi.label}</p>
            </div>
          );
        })}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Pipeline Funnel */}
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Pipeline de recrutement</h3>
          <div className="space-y-2">
            {pipelineOrder.map(status => {
              const count = pipeline[status] || 0;
              const cfg = STATUS_CONFIG[status] || {};
              const pct = (count / maxPipeline) * 100;
              return (
                <div key={status} className="flex items-center gap-3">
                  <span className="text-xs text-gray-600 w-28 truncate">{cfg.label || status}</span>
                  <div className="flex-1 bg-gray-100 rounded-full h-5 relative">
                    <div className={`h-full rounded-full ${status === 'embauché' ? 'bg-green-400' : status === 'refusé' ? 'bg-red-300' : 'bg-violet-400'} transition-all`} style={{ width: `${Math.max(pct, 2)}%` }} />
                    <span className="absolute right-2 top-0.5 text-xs font-medium text-gray-700">{count}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Sources */}
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Sources des candidatures</h3>
          <div className="space-y-2">
            {Object.entries(sources).sort((a, b) => b[1] - a[1]).map(([source, count]) => (
              <div key={source} className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{source}</span>
                <span className="text-sm font-semibold text-gray-800">{count}</span>
              </div>
            ))}
            {Object.keys(sources).length === 0 && <p className="text-sm text-gray-400">Aucune candidature</p>}
          </div>
        </div>
      </div>

      {/* Recent comments */}
      {data.recentComments?.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Activité récente</h3>
          <div className="space-y-2">
            {data.recentComments.slice(0, 5).map(c => (
              <div key={c.id} className="flex items-start gap-2 text-sm">
                <MessageSquare size={14} className="text-violet-400 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="font-medium text-gray-700">{c.authorName}</span>
                  <span className="text-gray-400"> sur </span>
                  <span className="text-violet-600">{c.application?.candidateName}</span>
                  <span className="text-gray-400"> — {fmtDate(c.createdAt)}</span>
                  <p className="text-gray-500 text-xs mt-0.5 line-clamp-1">{c.content}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Offers Tab ───────────────────────────────────────────────────────────────

const OffersTab = ({ offers, structures, showAllStructures, onEdit, onDelete, onStatusChange, onAddPublication, onDeletePublication, onAdd }) => {
  const [expandedOffer, setExpandedOffer] = useState(null);

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-gray-700">{offers.length} offre{offers.length > 1 ? 's' : ''}</h2>
        <button onClick={onAdd} className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg text-sm hover:bg-violet-700">
          <Plus size={16} /> Nouvelle offre
        </button>
      </div>
      <div className="space-y-4">
        {offers.map(offer => (
          <div key={offer.id} className="bg-white rounded-lg shadow">
            <div className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-gray-800">{offer.title}</h3>
                    {offer._count?.applications > 0 && (
                      <span className="text-xs bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full">{offer._count.applications} candidature{offer._count.applications > 1 ? 's' : ''}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-500 mt-1">
                    <span>{offer.contractType}</span>
                    {offer.salary && <span>{offer.salary}</span>}
                    {offer.workHours && <span>{offer.workHours}</span>}
                    {offer.location && <span className="flex items-center gap-1"><MapPin size={12} />{offer.location}</span>}
                    {offer.structure && <span className="flex items-center gap-1"><Building size={12} />{offer.structure.name}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <select
                    value={offer.status}
                    onChange={(e) => onStatusChange(offer.id, e.target.value)}
                    className={`text-xs font-medium px-2 py-1 rounded-full border-0 cursor-pointer ${OFFER_STATUS[offer.status]?.color || 'bg-gray-100'}`}
                  >
                    {Object.entries(OFFER_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                  <button onClick={() => onAddPublication(offer.id)} className="p-1.5 text-gray-400 hover:text-violet-600" title="Ajouter publication">
                    <Globe size={16} />
                  </button>
                  <button onClick={() => onEdit(offer)} className="p-1.5 text-gray-400 hover:text-blue-600" title="Modifier">
                    <Edit size={16} />
                  </button>
                  <button onClick={() => onDelete(offer.id)} className="p-1.5 text-gray-400 hover:text-red-600" title="Supprimer">
                    <Trash2 size={16} />
                  </button>
                  <button onClick={() => setExpandedOffer(expandedOffer === offer.id ? null : offer.id)} className="p-1.5 text-gray-400">
                    {expandedOffer === offer.id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </button>
                </div>
              </div>

              {/* Publications */}
              {offer.publications?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {offer.publications.map(pub => {
                    const pf = PLATFORMS.find(p => p.value === pub.platform);
                    return (
                      <div key={pub.id} className="flex items-center gap-1 bg-gray-100 rounded-full px-3 py-1 text-xs">
                        <Globe size={12} className="text-gray-500" />
                        <span>{pf?.label || pub.platform}</span>
                        {pub.publishedAt && <span className="text-gray-400">• {fmtDate(pub.publishedAt)}</span>}
                        {pub.url && <a href={pub.url} target="_blank" rel="noopener noreferrer" className="text-violet-500 hover:text-violet-700"><ExternalLink size={12} /></a>}
                        <button onClick={(e) => { e.stopPropagation(); onDeletePublication(offer.id, pub.id); }} className="text-gray-400 hover:text-red-500 ml-1"><X size={12} /></button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Expanded details */}
            {expandedOffer === offer.id && (
              <div className="border-t px-4 py-3 bg-gray-50 text-sm text-gray-600">
                <div className="mb-2">
                  <h4 className="font-medium text-gray-700 mb-1">Description</h4>
                  <p className="whitespace-pre-wrap">{offer.description}</p>
                </div>
                {offer.requirements && (
                  <div>
                    <h4 className="font-medium text-gray-700 mb-1">Prérequis</h4>
                    <p className="whitespace-pre-wrap">{offer.requirements}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
        {offers.length === 0 && (
          <div className="bg-white rounded-lg shadow p-8 text-center text-gray-400">
            <Briefcase size={40} className="mx-auto mb-3 opacity-30" />
            <p>Aucune offre d'emploi</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Interviews Tab ───────────────────────────────────────────────────────────

const InterviewsTab = ({ interviews, templates, applications, structures, onRefresh, onPlanInterview }) => {
  const toast = useToast();
  const [completingId, setCompletingId] = useState(null);
  const [completeForm, setCompleteForm] = useState({ notes: '', rating: 3, outcome: 'passed' });

  const handleComplete = async () => {
    try {
      await completeRecruitmentInterview(completingId, completeForm);
      toast.success('Entretien complété');
      setCompletingId(null);
      onRefresh();
    } catch (e) { toast.error('Erreur'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet entretien ?')) return;
    try {
      await deleteRecruitmentInterview(id);
      toast.success('Entretien supprimé');
      onRefresh();
    } catch (e) { toast.error('Erreur'); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-gray-700">{interviews.length} entretien{interviews.length > 1 ? 's' : ''} à venir</h2>
        <button onClick={() => onPlanInterview()} className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg text-sm hover:bg-violet-700">
          <Plus size={16} /> Planifier
        </button>
      </div>

      <div className="space-y-3">
        {interviews.map(itw => (
          <div key={itw.id} className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${itw.round === 1 ? 'bg-blue-100 text-blue-700' : itw.round === 2 ? 'bg-purple-100 text-purple-700' : 'bg-teal-100 text-teal-700'}`}>
                    Round {itw.round} — {ROUND_LABELS[itw.round]}
                  </span>
                  <span className="font-medium text-gray-800">{itw.application?.candidateName}</span>
                </div>
                <div className="flex items-center gap-4 text-xs text-gray-500 mt-1">
                  <span>{itw.application?.position}</span>
                  {itw.scheduledAt && <span className="flex items-center gap-1"><Clock size={12} />{fmtDateTime(itw.scheduledAt)}</span>}
                  {itw.interviewers && <span>{itw.interviewers}</span>}
                  {itw.structure && <span className="flex items-center gap-1"><Building size={12} />{itw.structure.name}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!itw.completedAt && (
                  <button onClick={() => { setCompletingId(itw.id); setCompleteForm({ notes: '', rating: 3, outcome: 'passed' }); }}
                    className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded-lg hover:bg-green-200">
                    Compléter
                  </button>
                )}
                <button onClick={() => handleDelete(itw.id)} className="p-1 text-gray-400 hover:text-red-600"><Trash2 size={14} /></button>
              </div>
            </div>
          </div>
        ))}
        {interviews.length === 0 && (
          <div className="bg-white rounded-lg shadow p-8 text-center text-gray-400">
            <Calendar size={40} className="mx-auto mb-3 opacity-30" />
            <p>Aucun entretien planifié</p>
          </div>
        )}
      </div>

      {/* Complete Modal */}
      <Modal isOpen={!!completingId} onClose={() => setCompletingId(null)} title="Completer l'entretien" size="md" preventClose={true}>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Note globale</label>
            <RatingStars rating={completeForm.rating} onChange={r => setCompleteForm(f => ({ ...f, rating: r }))} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Résultat</label>
            <select value={completeForm.outcome} onChange={e => setCompleteForm(f => ({ ...f, outcome: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
              <option value="passed">Retenu pour la suite</option>
              <option value="failed">Non retenu</option>
              <option value="pending">En attente de décision</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea rows={4} value={completeForm.notes} onChange={e => setCompleteForm(f => ({ ...f, notes: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Impressions, points forts/faibles..." />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => setCompletingId(null)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Annuler</button>
            <button onClick={handleComplete} className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700">Valider</button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

// ─── Pending CVs Tab ──────────────────────────────────────────────────────────

const PendingCVsTab = ({ pendingCVs, onApprove, onReject, offers, structures }) => (
  <div>
    <h2 className="text-lg font-semibold text-gray-700 mb-4">{pendingCVs.length} CV en attente de validation</h2>
    <div className="space-y-3">
      {pendingCVs.map(cv => (
        <div key={cv.id} className="bg-white rounded-lg shadow p-4 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <FileText size={16} className="text-violet-500" />
              <span className="font-medium text-gray-800">{cv.fileName}</span>
              {cv.candidateName && <span className="text-sm text-gray-500">— {cv.candidateName}</span>}
            </div>
            <div className="text-xs text-gray-400 mt-1">
              Source : {cv.source} • Reçu le {fmtDate(cv.createdAt)}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a href={cv.fileUrl} target="_blank" rel="noopener noreferrer" className="p-1.5 text-gray-400 hover:text-blue-600" title="Voir le fichier">
              <Eye size={16} />
            </a>
            <button onClick={() => onApprove(cv)} className="px-3 py-1.5 text-xs bg-green-100 text-green-700 rounded-lg hover:bg-green-200">
              Approuver
            </button>
            <button onClick={() => onReject(cv.id)} className="px-3 py-1.5 text-xs bg-red-100 text-red-600 rounded-lg hover:bg-red-200">
              Rejeter
            </button>
          </div>
        </div>
      ))}
      {pendingCVs.length === 0 && (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-400">
          <FileText size={40} className="mx-auto mb-3 opacity-30" />
          <p>Aucun CV en attente</p>
          <p className="text-xs mt-1">Les CV reçus via Google Drive (N8N) apparaîtront ici</p>
        </div>
      )}
    </div>
  </div>
);

export default RecruitmentPage;
